import { useState, useEffect } from 'react';
import { ArrowRight, Shield, Users, TrendingUp, Star, DollarSign, Gift, UserCheck, CheckCircle, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import Header from '@/react-app/components/Header';
import Footer from '@/react-app/components/Footer';
import SavingsPackageCard from '@/react-app/components/SavingsPackageCard';
import type { SavingsPackage } from '@/shared/types';
import { api } from '@/shared/api/endpoints';
import { useAuthStore } from '@/shared/stores/authStore';
import trade1 from "../../assets/man-1.jpeg";
import trade2 from "../../assets/man-2.jpeg";
import trade3 from "../../assets/man-3.jpeg";
import { useNavigate } from 'react-router';
import dashboardPreview from "../../assets/dashboard-preview.png";
import promo1 from "../../../public/promo_1.jpeg";
import savingsGrowth from "../../assets/savings-growth-charts.png";
import communitySavings from "../../assets/community-savings.png";
import securityTrust from "../../assets/security-trust.png";
import wealthBuilding from "../../assets/wealth-building.png";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, EffectFade } from 'swiper/modules';
import 'swiper/swiper-bundle.css';
// import 'swiper/css';
// import 'swiper/css/pagination';
// import 'swiper/css/effect-fade';

export default function Landing() {
  const { isAuthenticated } = useAuthStore();
  const [packages, setPackages] = useState<SavingsPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await api.package.getAll();
      if (response.success && response.data) {
        setPackages(response.data || []);
      }

    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleGetStarted = (ride: string) => {
    if (ride == "learn") {
      navigate('/about');
    } else {

      if (isAuthenticated) {
        navigate('/save');
      } else {
        navigate('/login');
      }
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Header />

      {/* Hero Section */}
      <section id="home" className="pt-16 overflow-hidden">
        {/* Reduced min-height from screen to 80vh */}
        <div className="gradient-bg min-h-[80vh] flex items-center">
          {/* Reduced vertical padding from py-20 to py-12 */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

              {/* Text Content */}
              <div className="text-white space-y-6"> {/* Reduced space-y */}
                <div className="space-y-4">
                  <h1 className="text-4xl lg:text-5xl font-bold leading-tight">
                    Save Smarter. <br />
                    <span className="text-blue-200">Earn Monthly.</span> <br />
                    Build Wealth.
                  </h1>
                  <p className="text-lg text-blue-100 max-w-lg">
                    Join thousands of smart savers earning consistent monthly returns.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <button onClick={() => handleGetStarted("log")} className="bg-white text-blue-600 px-8 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-all shadow-xl">
                    Get Started
                  </button>
                  <button onClick={() => handleGetStarted("learn")} className="border-2 border-white text-white px-8 py-3 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transition-all">
                    Learn More
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-8 pt-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold">$2M+</div>
                    <div className="text-blue-200 text-xs">Total Saved</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">5K+</div>
                    <div className="text-blue-200 text-xs">Happy Savers</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold">10%</div>
                    <div className="text-blue-200 text-xs">Max Returns</div>
                  </div>
                </div>
              </div>

              {/* Carousel Preview Area */}
              <div className="relative">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-blue-900/20 backdrop-blur-sm p-2">
                  <Swiper
                    modules={[Autoplay, Pagination, EffectFade]}
                    effect={'fade'}
                    autoplay={{ delay: 3000, disableOnInteraction: false }}
                    pagination={{ clickable: true }}
                    className="rounded-2xl"
                  >
                    {/* Slide 1: Dashboard Preview */}
                    <SwiperSlide>
                      <img src={dashboardPreview} alt="Dashboard" className="w-full h-auto object-contain" />
                    </SwiperSlide>

                    {/* Slide 2: An Advertisement or Promo */}
                    <SwiperSlide>
                      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 aspect-[4/3] flex flex-col items-center justify-center p-8 text-white text-center">
                        <h3 className="text-2xl font-bold mb-2">Coming Soon! 🚀</h3>
                        <p>Get ready to embark on a wealth building adventure with TradeEnrich.</p>
                        <div className="mt-4 bg-white/20 p-4 rounded-lg">PROMO: WEALTH2026</div>
                      </div>
                    </SwiperSlide>

                    {/* Slide 3: App Features */}
                    <SwiperSlide>
                      <img src={promo1} alt="Features" className="w-full h-auto object-contain" />
                    </SwiperSlide>
                  </Swiper>
                </div>

                {/* Decorative element to keep the "float" feel */}
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-blue-400/20 blur-3xl rounded-full animate-pulse"></div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-gray-50 dark:bg-gray-800">{" "}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">How It Works</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Start earning in just 4 simple steps. Our streamlined process makes saving and earning effortless.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                icon: UserCheck,
                title: 'Sign Up',
                description: 'Create your account with Google OAuth in seconds'
              },
              {
                step: '02',
                icon: Star,
                title: 'Select Package',
                description: 'Choose the savings plan that fits your goals'
              },
              {
                step: '03',
                icon: DollarSign,
                title: 'Deposit',
                description: 'Make your first deposit starting from $35'
              },
              {
                step: '04',
                icon: TrendingUp,
                title: 'Earn Monthly',
                description: 'Watch your money grow with consistent returns'
              }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white dark:bg-gray-700 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 text-center">
                  <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-6">
                    <item.icon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="text-blue-600 dark:text-blue-400 font-bold text-sm mb-2">STEP {item.step}</div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{item.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300">{item.description}</p>
                </div>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="h-6 w-6 text-blue-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Savings Packages Section */}
      <section id="packages" className="py-20 bg-white dark:bg-gray-900 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute left-0 top-1/2 transform -translate-y-1/2 opacity-5 dark:opacity-3">
          <img
            src={savingsGrowth}
            alt="Savings Growth Charts"
            className="w-96 h-72 object-contain"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Choose Your Savings Plan</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Select from our range of savings packages designed to maximize your returns and build wealth over time.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 dark:text-gray-300 mt-4">Loading savings packages...</p>
            </div>
          ) : (
            // <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8">
            //   {packages.map((pkg, index) => (
            //     <SavingsPackageCard
            //       key={pkg._id}
            //       package={pkg}
            //       isPopular={index === 2} // Make Savings++ popular
            //       onSelect={() => handleGetStarted("log")}
            //       buttonText={isAuthenticated ? "Select Plan" : "Get Started"}
            //     />
            //   ))}
            // </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8">
              {[...packages]
                .sort((a, b) => a.minDeposit - b.minDeposit)
                .map((pkg, index) => (
                  <SavingsPackageCard
                    key={pkg._id}
                    package={pkg}
                    isPopular={index === 2} // still works after sorting
                    onSelect={() => handleGetStarted("log")}
                    buttonText={isAuthenticated ? "Select Plan" : "Get Started"}
                  />
                ))}
            </div>

          )}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">What Our Savers Say</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Join thousands of satisfied customers who are building wealth with Trade Enrich.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Oke Madu",
                role: "Marketing Manager",
                comment: "I have been earning a steady 10.5% monthly return with Savings ++. Watching my money grow every single month has been unbelievable.",
                rating: 5,
                avatar: trade1
              },
              {
                name: "Michael Chen",
                role: "Software Engineer",
                comment: "Started with Regular Savings and upgraded to Savings Royal. The referral bonuses are amazing too!",
                rating: 5,
                avatar: trade2
              },
              {
                name: "Emily Rodriguez",
                role: "Business Owner",
                comment: "Trade Enrich helped me build an emergency fund while earning monthly returns. Highly recommended!",
                rating: 5,
                avatar: trade3
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white dark:bg-gray-700 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-full overflow-hidden mr-4">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 dark:text-white">{testimonial.name}</h4>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">{testimonial.role}</p>
                  </div>
                </div>
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 dark:text-gray-300 italic">"{testimonial.comment}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Referral Program Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-20">
          <img
            src={communitySavings}
            alt="Community Savings"
            className="w-full h-96 object-cover"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center text-white">
            <Gift className="h-16 w-16 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-4">Earn More with Referrals</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Share your unique referral code and earn bonus returns when your friends join and start saving.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8">
                <Users className="h-12 w-12 text-blue-200 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Share Your Code</h3>
                <p className="text-blue-100">Get a unique referral code when you sign up</p>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8">
                <Gift className="h-12 w-12 text-blue-200 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Friends Join</h3>
                <p className="text-blue-100">Your friends use your code to register</p>
              </div>
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8">
                <TrendingUp className="h-12 w-12 text-blue-200 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3">Earn Bonuses</h3>
                <p className="text-blue-100">Get referral bonuses on their deposits</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust & Security Section */}
      <section className="py-20 bg-white dark:bg-gray-900 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-15 dark:opacity-8">
          <img
            src={securityTrust}
            alt="Security and Trust"
            className="w-96 h-96 object-contain"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="w-24 h-24 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="h-12 w-12 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Your Security is Our Priority</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              We use bank-level security measures to protect your funds and personal information.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: 'Bank-Level Encryption',
                description: '256-bit SSL encryption protects all your data and transactions'
              },
              {
                icon: CheckCircle,
                title: 'Secure Payments',
                description: 'Integrated with trusted payment providers Paystack & Blockrader'
              },
              {
                icon: Users,
                title: '24/7 Support',
                description: 'Our dedicated support team is always ready to help you'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white dark:bg-gray-700 rounded-2xl p-8 shadow-lg text-center">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-6">
                  <item.icon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{item.title}</h3>
                <p className="text-gray-600 dark:text-gray-300">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="h-10 w-10 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Get answers to the most common questions about our savings platform, security, and returns.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: "How quickly can I start earning returns on my savings?",
                answer: "You start earning returns immediately after your first deposit is processed and verified. Monthly returns are calculated and paid out at the beginning of each month. For example, if you deposit in January, you'll receive your first return payment in February."
              },
              {
                question: "What makes Trade Enrich different from traditional banks?",
                answer: "Unlike traditional banks that offer minimal interest rates (often less than 1%), Trade Enrich provides significantly higher monthly returns through our investment strategies. We also offer referral bonuses, transparent fee structures, and a user-friendly digital platform designed specifically for modern savers."
              },
              {
                question: "How secure is my money with Trade Enrich?",
                answer: "Your security is our top priority. We use bank-level 256-bit SSL encryption, multi-factor authentication, and store funds with regulated financial institutions. Our platform undergoes regular security audits, and we're compliant with financial regulations including KYC and AML requirements."
              },
              {
                question: "Can I withdraw my money anytime?",
                answer: "Yes, you can request withdrawals anytime through your dashboard. Standard withdrawals are processed within 3-5 business days. However, we recommend keeping funds invested for at least a few months to maximize your returns and benefit from compound growth."
              },
              {
                question: "What are the minimum deposit requirements?",
                answer: "Our savings packages start from as low as $500 for Regular Savings. We offer various packages to suit different budgets: Regular Savings ($500), Savings+ ($1,000), Savings++ ($2,500), Savings Premium ($5,000), and Savings Royal ($10,000)."
              },
              {
                question: "How does the referral program work?",
                answer: "When you sign up, you'll receive a unique referral code. Share this code with friends and family. When they join using your code and make their first deposit, you'll earn a referral bonus based on your current savings package. The more friends you refer, the more bonuses you earn!"
              },
              {
                question: "Are the monthly returns guaranteed?",
                answer: "While we strive to provide consistent returns based on our investment strategies and market performance, returns are not guaranteed and may vary based on market conditions. However, our track record shows consistent performance, and we're transparent about any changes to projected returns."
              },
              {
                question: "What payment methods do you accept?",
                answer: "We accept various payment methods including bank transfers, debit cards, and payments through our trusted partners Paystack and Blockrader. All transactions are secure and encrypted. We also support multiple currencies for international users."
              },
              {
                question: "Do you charge any fees?",
                answer: "We believe in transparency. There are no hidden fees for deposits or monthly returns. We may charge small processing fees for withdrawals (clearly disclosed before confirmation) and premium features. All fees are clearly outlined in your dashboard before any transaction."
              },
              {
                question: "How can I track my earnings and account balance?",
                answer: "Your personalized dashboard provides real-time tracking of your balance, monthly returns, referral bonuses, and transaction history. You'll also receive monthly statements and can set up notifications for important account activities."
              },
              {
                question: "What happens if I want to upgrade my savings package?",
                answer: "You can upgrade your savings package anytime by making an additional deposit that meets the higher tier requirements. Your new returns will be calculated based on your total balance and the new package benefits. Contact our support team for assistance with upgrades."
              },
              {
                question: "Is there customer support available?",
                answer: "Yes! Our dedicated customer support team is available 24/7 via email, phone, and live chat. We're committed to helping you with any questions about your account, transactions, or our services. You can reach us at support@tradeenrich.com or +1 (555) 123-4567."
              }
            ].map((faq, index) => (
              <div key={index} className="bg-white dark:bg-gray-700 rounded-xl shadow-lg overflow-hidden">
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full px-6 py-6 text-left hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white pr-8">
                      {faq.question}
                    </h3>
                    {openFAQ === index ? (
                      <ChevronUp className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-6">
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contact Support CTA */}
          <div className="mt-12 text-center">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                Still have questions?
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Our friendly support team is here to help you get started on your savings journey.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/contact"
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors inline-flex items-center justify-center"
                >
                  Contact Support
                </a>
                <a
                  href="mailto:support@tradeenrich.com"
                  className="border border-blue-600 text-blue-600 dark:text-blue-400 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors inline-flex items-center justify-center"
                >
                  Send Email
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-10">
          <img
            src={wealthBuilding}
            alt="Wealth Building"
            className="w-full h-96 object-cover"
          />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Start Building Wealth?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join thousands of smart savers who are already earning consistent monthly returns.
          </p>
          <button
            onClick={() => handleGetStarted("log")}
            className="bg-blue-600 text-white px-12 py-4 rounded-xl text-lg font-semibold hover:bg-blue-700 transition-all duration-300 shadow-2xl hover:shadow-3xl"
          >
            Start Saving Today
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
